// src/generators/dzial1_liczby_i_dzialania/temat1_dzialania/podtemat3_zbiory_types/SetOpsFourResultsTask.js
import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FiniteSetInput, { parseFiniteSetInput, finiteSetToLatex } from '../../../../components/inputs/FiniteSetInput';

const U_MIN = -9;
const U_MAX = 9;
const SIZE_MIN = 3;
const SIZE_MAX = 6;

const rand = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

function shuffled(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function choose(arr, n) {
  const count = Math.max(0, Math.min(n, arr.length));
  return shuffled(arr).slice(0, count);
}

function setUnion(A, B) {
  return Array.from(new Set([...A, ...B])).sort((a, b) => a - b);
}
function setIntersect(A, B) {
  const SB = new Set(B);
  return A.filter(x => SB.has(x)).sort((a, b) => a - b);
}
function setDiff(A, B) {
  const SB = new Set(B);
  return A.filter(x => !SB.has(x)).sort((a, b) => a - b);
}

// rozkład dla |A∩B| (przycinany do max)
function sampleK(maxK) {
  const base = [
    { k: 0, p: 0.10 },
    { k: 1, p: 0.40 },
    { k: 2, p: 0.30 },
    { k: 3, p: 0.15 },
    { k: 4, p: 0.05 },
  ].filter(x => x.k <= maxK);
  const sum = base.reduce((s, x) => s + x.p, 0);
  let r = Math.random() * sum;
  for (const x of base) { if ((r -= x.p) <= 0) return x.k; }
  return base[base.length - 1].k;
}

/* ===== generator ===== */
export function generateTask() {
  const U = Array.from({ length: U_MAX - U_MIN + 1 }, (_, i) => U_MIN + i);

  const sizeA = rand(SIZE_MIN, SIZE_MAX);
  const sizeB = rand(SIZE_MIN, SIZE_MAX);
  const maxK = Math.min(sizeA, sizeB);
  const k = sampleK(maxK);

  const S = choose(U, k);
  const U_wo_S = U.filter(x => !S.includes(x));
  const restA = choose(U_wo_S, sizeA - k);
  const U_wo_SA = U_wo_S.filter(x => !restA.includes(x));
  const restB = choose(U_wo_SA, sizeB - k);

  let A = [...S, ...restA].sort((a, b) => a - b);
  let B = [...S, ...restB].sort((a, b) => a - b);

  // uniknij A=B (podmień jeden element w B, jeśli się zdarzy)
  if (A.length === B.length && A.every((x, i) => x === B[i])) {
    const candidates = U.filter(x => !A.includes(x));
    if (candidates.length) {
      B = [...B];
      B[B.length - 1] = candidates[0];
      B.sort((a, b) => a - b);
    }
  }

  return {
    type: 'SetOpsFourResultsTask',
    data: { A, B }, // zawsze A i B
  };
}

/* ===== render zadania ===== */
export function renderTask({ data }) {
  const { A, B } = data;
  return (
    <div>
      <p>
        Dane są zbiory{' '}
        <InlineMath math={`A = ${finiteSetToLatex(A)}`} /> oraz{' '}
        <InlineMath math={`B = ${finiteSetToLatex(B)}`} />.
      </p>
      <p style={{ marginTop: 8 }}>
        <strong>Wypisz elementy:</strong>{' '}
        <InlineMath math={`A \\cup B`} />,{' '}
        <InlineMath math={`A \\cap B`} />,{' '}
        <InlineMath math={`A \\setminus B`} />,{' '}
        <InlineMath math={`B \\setminus A`} />.
      </p>
    </div>
  );
}

/* ===== input ===== */
export function renderInput(_, userAnswer, onChange, disabled) {
  const ua = userAnswer ?? {};
  const setField = (key) => (val) => onChange({ ...(ua || {}), [key]: val });

  // Bez słownych etykiet. Odstępy robione ręcznie (pewne).
  return (
    <div style={{ display: 'grid', rowGap: 12 }}>
      <div style={{ display: 'grid', rowGap: 6 }}>
        <div><InlineMath math={'A \\cup B'} /></div>
        <FiniteSetInput
          value={ua?.union}
          onChange={setField('union')}
          disabled={disabled}
          placeholder={'np. { -3, -1, 0, 2, 5 }'}
          ariaLabel='suma A∪B'
          showPreview
          showKeyboard
        />
      </div>

      <div style={{ display: 'grid', rowGap: 6 }}>
        <div><InlineMath math={'A \\cap B'} /></div>
        <FiniteSetInput
          value={ua?.inter}
          onChange={setField('inter')}
          disabled={disabled}
          placeholder={'np. { -1, 2 }'}
          ariaLabel='część wspólna A∩B'
          showPreview
          showKeyboard
        />
      </div>

      <div style={{ display: 'grid', rowGap: 6 }}>
        <div><InlineMath math={'A \\setminus B'} /></div>
        <FiniteSetInput
          value={ua?.diffAB}
          onChange={setField('diffAB')}
          disabled={disabled}
          placeholder={'np. { -3, 0, 5 }'}
          ariaLabel='różnica A\\B'
          showPreview
          showKeyboard
        />
      </div>

      <div style={{ display: 'grid', rowGap: 6 }}>
        <div><InlineMath math={'B \\setminus A'} /></div>
        <FiniteSetInput
          value={ua?.diffBA}
          onChange={setField('diffBA')}
          disabled={disabled}
          placeholder={'np. { -8, 4 }'}
          ariaLabel='różnica B\\A'
          showPreview
          showKeyboard
        />
      </div>
    </div>
  );
}

/* ===== check ===== */
export function checkAnswer({ data }, userAnswer) {
  const norm = (field) => {
    const v = userAnswer && typeof userAnswer === 'object' ? userAnswer[field] : undefined;
    if (v && typeof v === 'object') return v.parsed;
    return parseFiniteSetInput(v);
  };

  const gotUnion  = norm('union');
  const gotInter  = norm('inter');
  const gotDiffAB = norm('diffAB');
  const gotDiffBA = norm('diffBA');

  const { A, B } = data;

  const SA = new Set(A);
  const SB = new Set(B);

  const expUnion  = Array.from(new Set([...A, ...B])).sort((a, b) => a - b);
  const expInter  = A.filter(x => SB.has(x)).sort((a, b) => a - b);
  const expDiffAB = A.filter(x => !SB.has(x)).sort((a, b) => a - b);
  const expDiffBA = B.filter(x => !SA.has(x)).sort((a, b) => a - b);

  const eq = (X, Y) => Array.isArray(X) && X.length === Y.length && X.every((x, i) => x === Y[i]);

  const uOk = eq(gotUnion,  expUnion);
  const iOk = eq(gotInter,  expInter);
  const aOk = eq(gotDiffAB, expDiffAB);
  const bOk = eq(gotDiffBA, expDiffBA);

  const parts = { 'A∪B': uOk, 'A∩B': iOk, 'A\\B': aOk, 'B\\A': bOk };
  const score = (uOk?1:0) + (iOk?1:0) + (aOk?1:0) + (bOk?1:0);
  return { isCorrect: score === 4, score, maxScore: 4, parts };
}

/* ===== answer / user answer ===== */
export function renderAnswer({ data }) {
  const { A, B } = data;
  const U = setUnion(A, B);
  const I = setIntersect(A, B);
  const DAB = setDiff(A, B);
  const DBA = setDiff(B, A);

  // ręczne odstępy: 16px od góry, 2px między liniami
  return (
    <div style={{ marginTop: 16, display: 'grid', rowGap: 2, lineHeight: 1.35 }}>
      <div><InlineMath math={`A \\cup B = ${finiteSetToLatex(U)}`} /></div>
      <div><InlineMath math={`A \\cap B = ${finiteSetToLatex(I)}`} /></div>
      <div><InlineMath math={`A \\setminus B = ${finiteSetToLatex(DAB)}`} /></div>
      <div><InlineMath math={`B \\setminus A = ${finiteSetToLatex(DBA)}`} /></div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const one = (labelLatex, v) => {
    const raw = typeof v === 'object' ? (v?.raw ?? '') : (v ?? '');
    const parsed = typeof v === 'object' ? v?.parsed : parseFiniteSetInput(v);
    if (parsed === undefined || raw.trim() === '') return <div><InlineMath math={labelLatex} /> <i>(brak odpowiedzi)</i></div>;
    if (parsed === null) return <div><InlineMath math={labelLatex} /> <i>(niepoprawny format)</i></div>;
    return <div><InlineMath math={`${labelLatex}~${finiteSetToLatex(parsed)}`} /></div>;
  };
  const ua = userAnswer ?? {};

  // ręczne odstępy: 16px od góry, 2px między liniami
  return (
    <div style={{ marginTop: 16, display: 'grid', rowGap: 2, lineHeight: 1.35 }}>
      {one('A \\cup B =', ua.union)}
      {one('A \\cap B =', ua.inter)}
      {one('A \\setminus B =', ua.diffAB)}
      {one('B \\setminus A =', ua.diffBA)}
    </div>
  );
}

/* ===== explanation ===== */
export function renderExplanation({ data }) {
  const { A, B } = data;
  const U = setUnion(A, B);
  const I = setIntersect(A, B);
  const DAB = setDiff(A, B);
  const DBA = setDiff(B, A);

  return (
    <div style={{ display: 'grid', rowGap: 8 }}>
      <p><strong>Krok 1:</strong> Wypisz elementy A i B.</p>
      <BlockMath math={`A = ${finiteSetToLatex(A)}\\quad\\; B = ${finiteSetToLatex(B)}`} />
      <p><strong>Krok 2:</strong> <InlineMath math={`A \\cap B`} /> – elementy wspólne:</p>
      <BlockMath math={`A \\cap B = ${finiteSetToLatex(I)}`} />
      <p><strong>Krok 3:</strong> <InlineMath math={`A \\cup B`} /> – wszystkie elementy bez powtórzeń:</p>
      <BlockMath math={`A \\cup B = ${finiteSetToLatex(U)}`} />
      <p><strong>Krok 4:</strong> Różnice zbiorów:</p>
      <BlockMath math={`A \\setminus B = ${finiteSetToLatex(DAB)}\\quad\\; B \\setminus A = ${finiteSetToLatex(DBA)}`} />
    </div>
  );
}
